import {useState, forwardRef, useEffect} from "react";

import {useTiptapEditor} from "components/RichEditor/hooks/use-tiptap-editor";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "components/RichEditor/components/tiptap-ui-primitive/popover";
import {
  Button,
  ButtonGroup,
} from "components/RichEditor/components/tiptap-ui-primitive/button";
import {
  Card,
  CardBody,
  CardItemGroup,
} from "components/RichEditor/components/tiptap-ui-primitive/card";
import {
  TextAlignButton,
  useTextAlign,
} from "components/RichEditor/components/tiptap-ui/text-align-button";

// icons
import {AlignLeftIcon} from "components/RichEditor/components/tiptap-icons/align-left-icon";
import {AlignCenterIcon} from "components/RichEditor/components/tiptap-icons/align-center-icon";
import {AlignRightIcon} from "components/RichEditor/components/tiptap-icons/align-right-icon";
import {AlignJustifyIcon} from "components/RichEditor/components/tiptap-icons/align-justify-icon";
import {ChevronDownIcon} from "components/RichEditor/components/tiptap-icons/chevron-down-icon";

const ALIGN_ICONS = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
  justify: AlignJustifyIcon,
};

export const TextAlignPopoverButton = forwardRef(
  ({editor, onAligned, hideWhenUnavailable = false, align = "left", ...props}, ref) => {
    const Icon = ALIGN_ICONS[align];

    const {isActive} = useTextAlign({
      editor,
      align,
      hideWhenUnavailable,
      onAligned,
    });

    return (
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        data-active-state={isActive ? "on" : "off"}
        aria-label="Text align"
        aria-pressed={isActive}
        tooltip="RICH_EDITOR_TOOLBAR_TEXT_ALIGN_BUTTON_TOOLTIP"
        {...props}
      >
        <Icon className="tiptap-button-icon" />
        <ChevronDownIcon className="tiptap-button-dropdown-small" />
      </Button>
    );
  }
);

TextAlignPopoverButton.displayName = "TextAlignPopoverButton";

export function TextAlignPopoverContent({editor, aligns, onSelect}) {
  return (
    <Card>
      <CardBody>
        <CardItemGroup orientation="horizontal">
          <ButtonGroup orientation="horizontal">
            {aligns.map((align) => (
              <TextAlignButton align={align} key={align} />
            ))}
          </ButtonGroup>
        </CardItemGroup>
      </CardBody>
    </Card>
  );
}

export function TextAlignPopover({
  editor: providedEditor,
  aligns = ["left", "center", "right", "justify"],
}) {
  const {editor} = useTiptapEditor(providedEditor);
  const [activeAlign, setActiveAlign] = useState("left");

  useEffect(() => {
    if (!editor) return;
    const update = () =>
      setActiveAlign(
        aligns.find((align) => editor.isActive({textAlign: align})) || "left"
      );

    update();
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);

    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor, aligns, setActiveAlign]);

  if (!editor) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <TextAlignPopoverButton
          editor={editor}
          align={activeAlign}
          aria-label="Text align"
        />
      </PopoverTrigger>

      <PopoverContent aria-label="Text align" style={{zIndex: 9999}}>
        <TextAlignPopoverContent editor={editor} aligns={aligns} />
      </PopoverContent>
    </Popover>
  );
}

export default TextAlignPopover;
